import { Box, Button, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import { exportMeetingsAuditReport } from '../../../utils/auditing-export-utilities'
import { Icons, leadingZeroFormatter } from '../../../utils/utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import AuditPageStatsFilterBar from '../AuditPageStatsFilterBar'
import noMeetingsImg from '../../../assets/no-graph.svg'
import LineBarGraph from '../../common/LineBarGraph'
import api from '../../../service/api'

const facultyNameSortComparator = ( a, b ) => {
    const sa = String( a?.name || "" ).toLowerCase()
    const sb = String( b?.name || "" ).toLowerCase()

    if ( sa < sb ) return -1
    if ( sa > sb ) return 1
    return 0
}



const columns = [
    { id: 1, index: true, field: "index", name: 'Sl. No.', align: "left", padding: "10px 20px" },
    { id: 3, field: "name", sortable: true, renderCell: row => row?.name?.toLowerCase(), comparator: facultyNameSortComparator, filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.empcode}?legacy=${Boolean( row.legacy_system_data )}`, name: 'Faculty', padding: "10px 20px", cellStyles: { textTransform: "capitalize" } },
    { id: 6, field: "empcode", sortable: true, filterable: true, linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row.empcode}?legacy=${Boolean( row.legacy_system_data )}`, name: "Employee ID", minWidth: "150px", padding: "10px 20px" },
    { id: 2, field: "institute", sortable: true, filterable: true, renderCell: row => `${row.institute} - ${row.department?.toUpperCase()}`, name: "Department", minWidth: "150px", padding: "10px 20px" },
    { id: 4, field: "meetings_count", align: "center", style: { align: "center" }, name: "Meetings Conducted", minWidth: "180px", padding: "10px 20px" },
]


const MeetingsStats = () => {

    const [searchParams] = useSearchParams( { sortByDept: "0", searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "0", sortBySubmissionsCount: "0", sortByMeetingsCount: "0" } )
    const month = searchParams.get( 'month' ) || ""
    const view = searchParams.get( 'view' ) || "tabular"
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [loading, setLoading] = useState( true )
    const [graphData, setGraphData] = useState( [] )
    const [statsError, setStatsError] = useState( null )
    const [graphError, setGraphError] = useState( null )
    const [statsData, setStatsData] = useState( {} )
    const [loadingGraphData, setLoadingGraphData] = useState( true )

    // Derive each section of the stats response from the single statsData object
    const { facultyStats, subjectStats, last12MonthsStats } = useMemo( () => {
        const { facultyStats = [], subjectStats = [], last12MonthsStats = {} } = statsData || {}
        return {
            facultyStats: Array.isArray( facultyStats ) ? facultyStats : [],
            subjectStats: Array.isArray( subjectStats ) ? subjectStats : [],
            last12MonthsStats: last12MonthsStats && Object.keys( last12MonthsStats ).length > 0 ? last12MonthsStats : null,
        }
    }, [statsData] )

    const filtersExists = Boolean( ( selectedInstitute && selectedInstitute !== "all" ) || ( selectedDepartment && selectedDepartment !== "all" ) )

    const { layout, palette } = useTheme()
    const { pathname } = useLocation()

    const filterFunc = item => {
        if ( selectedDepartment !== "all" ) {
            const [department_id] = selectedDepartment?.split( "-" )
            return +department_id === +item.department_id
        }
        return true
    }



    const getGraphData = useCallback( async () => {
        try {
            setLoadingGraphData( true )
            const institute = selectedInstitute !== "all" ? selectedInstitute : ""
            const department = selectedDepartment !== "all" ? selectedDepartment : ""
            const { data: graphDetails } = await api.audit.getMeetingsGraph( { month, institute, department, types: pathname.includes( 'online-classes' ) ? ["class"] : pathname.includes( 'hybrid-classes' ) ? ["hybrid-class"] : ["webinar", "discussion"] } )
            setGraphData( graphDetails.data )
            setGraphError( null )
        } catch ( err ) {
            setGraphError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the meetings graph data." } )
        } finally {
            setLoadingGraphData( false )
        }
    }, [month, selectedInstitute, selectedDepartment, pathname] )


    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getMeetingsStats( { month, institute: selectedInstitute !== "all" ? selectedInstitute : "", types: pathname.includes( 'online-classes' ) ? ["class"] : pathname.includes( 'hybrid-classes' ) ? ["hybrid-class"] : ["webinar", "discussion"] } )
            setStatsData( data || {} )
            setStatsError( null )
        } catch ( err ) {
            setStatsError( { code: err?.response?.status, message: err?.response?.data?.message || "Something went wrong while fetching the meetings audit data." } )
        } finally {
            setLoading( false )
        }
    }, [month, selectedInstitute, pathname] )

    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        getGraphData()
    }, [getGraphData] )

    const filtered = facultyStats.filter( filterFunc )
    const sessionType = pathname.match( /online-classes/ ) ? "online classes" : pathname.match( /hybrid-classes/ ) ? "hybrid classes" : "meetings"

    return (
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: layout.cardsGap }}>

            {/* Filters */}
            <AuditPageStatsFilterBar
                showZeroResourcesSwitch={false}
                resource={pathname.match( /online-classes/ ) ? "online classes" : pathname.match( /hybrid-classes/ ) ? "hybrid classes" : "meetings"}
                listResourceBtnLabel={pathname.match( /online-classes/ ) ? "View online classes" : pathname.match( /hybrid-classes/ ) ? "View hybrid classes" : "View Meetings"}
                title={pathname.match( /online-classes/ ) ? "Online classes statistics" : pathname.match( /hybrid-classes/ ) ? "Hybrid classes statistics" : "Meetings statistics"}
            />


            {!statsError && !loading && view === 'tabular' && <CustomTable
                hasFilters={selectedDepartment !== "all" || selectedInstitute !== "all"}
                globalSearch="Search by faculty name or employee code..."
                pageSize={50}
                tableStyles={{
                    maxHeight: "450px"
                }}
                data={filtered}
                columns={columns}
                customActions={[
                    {
                        type: "button",
                        label: "Export",
                        startIcon: Icons.default.Publish,
                        // rows = faculty rows after the table's search/sort, so the export matches what is on screen
                        onClick: ( rows ) => exportMeetingsAuditReport( { facultyStats: rows, subjectStats, last12MonthsStats }, `${month || "complete"}-${selectedInstitute !== "all" ? selectedInstitute + "-" : ""}${selectedDepartment !== "all" ? selectedDepartment : "alldept"}-meetings-audit-report`, sessionType.toUpperCase() )
                    }
                ]}
            />}

            {statsError && <TryAgainMessageBlock err={statsError.message} loading={loading} getData={getData} code={statsError.code} />}

            {!loading && !loadingGraphData && view !== 'tabular' && filtered.length > 0 &&
                <Box display="flex" color={palette.common.font} flexGrow={1} minHeight="400px">
                    {!graphError ? <LineBarGraph graphTitle="" barLegend='Participants' yAxisName="Meetings" xAxisName="Date" y2AxisName="Participants" lineLegend='Meetings created' barValuesKey="participants_count" barData={graphData} dateformat={"%Y-%m-%d"} xAxisValuesKey={'date'} lineValuesKey={'meetings_count'} lineData={graphData} />
                        : <TryAgainMessageBlock err={graphError.message} loading={loadingGraphData} getData={getGraphData} code={graphError.code} />}
                </Box>
            }
            {!loading && !loadingGraphData && view !== 'tabular' && filtered.length === 0 &&
                <Box flexDirection="column" padding="20px" display="flex" bgcolor={palette.contentBg} borderRadius={layout.borderRadius} alignItems="center" justifyContent="center" gap="20px">
                    <Box component="img" src={noMeetingsImg} height="120px" alt="No data to show" />
                    <Typography textAlign="center" variant='subtitle2'>No data found{filtersExists ? " for applied filters!" : " for the date range!"}</Typography>
                </Box>
            }
            {( loading || loadingGraphData ) && !statsError && !graphError && <DataLoadingSpinner waitingMessage="Loading meetings audit report please wait..." />}
        </Box >
    )
}

export default MeetingsStats